/**
 * Behaviour fixes for the Wrapped gift box (<wrapped-extension>), on the product page
 * and in the cart drawer. Loaded from layout/theme.liquid.
 *
 * Wrapped renders into an open shadow root, so these lean on its internals as of
 * build 2720. Each step checks for what it needs and otherwise leaves Wrapped's own
 * behaviour alone, so an app update degrades to stock rather than breaking.
 *
 * 1. Clicking the "Include a gift box" text only opens the options panel; only the box
 *    itself ticks (and, on the product page, saves). Make the text tick the box while
 *    the item isn't gifted yet; once it is, the text opens the panel as Wrapped intends.
 * 2. In the drawer, ticking the box opens the panel but Wrapped un-ticks it again until
 *    Save. Save straight away, as the product page does, so the tick sticks. A gift
 *    message can still be added and saved afterwards.
 *
 * Don't re-render the drawer after a save to "refresh" the gift box: Wrapped tears down
 * and rebuilds every block on any drawer re-render (a visible jump), and reloads the
 * whole page if a block's cart line is briefly missing during that rebuild.
 */
(() => {
  const isHost = (node) => node instanceof Element && node.localName === "wrapped-extension";
  const isDrawerHost = (node) => isHost(node) && node.getAttribute("target-group") === "drawer";

  // 1. The label wraps the checkbox, its checkmark, the text and the price. Runs in the
  // capture phase on document, before Wrapped's own label listener inside the shadow root.
  document.addEventListener(
    "click",
    (event) => {
      const path = event.composedPath();
      if (!path.some(isHost)) return;

      const label = path.find((node) => node instanceof Element && node.localName === "label");
      const input = label && label.querySelector(':scope > input[type="checkbox"]');
      // Once gifted, leave the text to Wrapped: it opens the panel with the saved message
      // and Remove. Forwarding it would untick the box and discard the message.
      if (!input || input.disabled || input.checked || path.includes(input)) return;

      event.preventDefault();
      event.stopPropagation();
      input.click();
    },
    true
  );

  // 2. Wrapped announces a tick with a composed "property:changed" event carrying `checked`.
  document.addEventListener("property:changed", (event) => {
    const host = event.target;
    if (!isDrawerHost(host) || !event.detail || event.detail.checked !== true) return;
    if (host.hasAttribute("saving") || typeof host.saveGifting !== "function") return;

    host.saveGifting();
  });

})();
